/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { WebSocketConnection } from '@app/core/services/websocket-connection.service';
import { joinMetricNameFragments, metricNameToString } from '@shared/domain/metrics';
import { ClusterId } from '@shared/types/cluster';
import { MetricDefinition, MetricValue, MetricsConfig, MetricsSubscribeHeaders } from '@shared/types/metrics';
import uniqBy from 'lodash-es/uniqBy';
import { Observable, merge } from 'rxjs';
import { MetricSelectSingleValue } from '../types/widgets';

type MetricsParams = {
  clusterId: ClusterId;
  metrics: MetricSelectSingleValue[];
  from?: number;
  to?: number;
  maxDataPoints?: number;
};

type MetricTopicHeaders = { 'from-ts': string; 'to-ts': string; 'max-data-points': string };

@Injectable({ providedIn: 'root' })
export class MetricApiService {
  private ws = inject(WebSocketConnection);
  private http = inject(HttpClient);

  metricDefinitions(clusterId: ClusterId): Observable<MetricDefinition[]> {
    return this.ws.getTopic<MetricDefinition[]>(`/topic/metric-descriptions/${clusterId}`);
  }

  metricValues(params: MetricsParams): Observable<MetricValue[]> {
    const args: { topic: string; headers: MetricTopicHeaders }[] = uniqBy(
      params.metrics,
      (arg) => arg.template,
    ).flatMap((metric) =>
      metric.nodeConsistentIds.map((nodeConsistentId) => ({
        topic: `/topic/metrics/${params.clusterId}/${encodeURIComponent(metricNameToString(joinMetricNameFragments(metric.template)))}/${encodeURIComponent(
          nodeConsistentId,
        )}`,
        headers: {
          'from-ts': params.from?.toString(10) ?? '',
          'to-ts': params.to?.toString(10) ?? '',
          'max-data-points': params.maxDataPoints?.toString(10) ?? '',
        } as MetricsSubscribeHeaders,
      })),
    );
    return merge(...args.map((arg) => this.ws.getTopic<MetricValue[]>(arg.topic, arg.headers)));
  }

  metricsConfig() {
    return this.http.get<MetricsConfig>('/api/v1/metrics/config');
  }
}
